import { datadogRum } from '@datadog/browser-rum';
import { decode as decodeMsgpack } from '@msgpack/msgpack';
import { DefaultError, QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { DeepNullable } from 'src/domain';
import { HttpStatusError } from 'src/domain/errors/HttpStatusError';
import { useAcquireIdToken } from 'src/hooks/auth/useAcquireIdToken';
import { useDevTools } from 'src/hooks/devtools/useDevTools';
import { paths } from './generated';

export type Path = keyof paths;

type PathMethod<T extends Path> = keyof paths[T];

type RequestParams<P extends Path, M extends PathMethod<P>> = paths[P][M] extends {
    // biome-ignore lint/suspicious/noExplicitAny: we need to use any
    parameters: any;
}
    ? paths[P][M]['parameters']
    : undefined;

type RequestBody<P extends Path, M extends PathMethod<P>> = paths[P][M] extends {
    requestBody: {
        content: {
            'application/json': infer R;
        };
    };
}
    ? R
    : undefined;

type OptionalRequestParams<P extends Path, M extends PathMethod<P>> =
    RequestParams<P, M> extends {
        query?: { [x: string]: unknown };
        path?: { [x: string]: unknown };
    }
        ? {
              query?: DeepNullable<RequestParams<P, M>['query']>;
              path?: DeepNullable<RequestParams<P, M>['path']>;
          }
        : RequestParams<P, M>;

export type ResponseType<P extends Path, M extends PathMethod<P>> = paths[P][M] extends {
    responses: {
        200: {
            content: {
                // biome-ignore lint/suspicious/noExplicitAny: we need to use any
                'application/json': any;
            };
        };
    };
}
    ? paths[P][M]['responses'][200]['content']['application/json']
    : paths[P][M] extends {
            responses: {
                200: {
                    content: {
                        // biome-ignore lint/suspicious/noExplicitAny: we need to use any
                        'application/x-msgpack': any;
                    };
                };
            };
        }
      ? paths[P][M]['responses'][200]['content']['application/x-msgpack']
      : undefined;

interface ApiImpersonateProps {
    email: string;
    roles: string[];
}

type ApiCallProps = {
    url: string;
    method: 'get' | 'post' | 'put' | 'delete';
    query?: Record<string, string>;
    body?: unknown;
    acquireToken: (forceRefresh?: boolean) => Promise<string | undefined>;
    impersonate?: ApiImpersonateProps;
};

function decodeResponse<Response>(res: globalThis.Response): Promise<Response> {
    const contentType = res.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/x-msgpack')) {
        return res.arrayBuffer().then((buffer) => decodeMsgpack(buffer) as Response);
    }
    return res.json().catch(() => ({}) as Response);
}

export async function apiCall<Response>(
    { url, method, query, body, acquireToken, impersonate }: ApiCallProps,
    retryUnauthorized = true
): Promise<Response> {
    const token = await acquireToken(!retryUnauthorized);
    if (!token) {
        throw new HttpStatusError(401, 'Unauthorized', {
            message: 'Unauthorized',
            errorType: 'InsufficientPermissions',
            statusCode: 401,
        });
    }
    return fetch(`${url}?${query ? new URLSearchParams(query) : ''}`, {
        method: method.toString(),
        body: body ? JSON.stringify(body) : undefined,
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/x-msgpack, application/json',
            Authorization: `Bearer ${token}`,
            ...(impersonate
                ? {
                      'X-Impersonate': impersonate.email,
                      'X-Impersonate-Roles': impersonate.roles.join(','),
                  }
                : {}),
        },
    }).then((res) => {
        if (res.status === 401 && retryUnauthorized) {
            return apiCall<Response>({ url, method, query, body, acquireToken, impersonate }, false);
        }

        if (!res.ok) {
            // handle http errors
            return res
                .json()
                .catch(() => undefined)
                .then((errorData) => {
                    throw new HttpStatusError(res.status, res.statusText, errorData);
                });
        }

        return decodeResponse<Response>(res);
    });
}

export function buildApiUrl(baseUrl: string, path: Record<string, unknown> = {}): string {
    return Object.keys(path).reduce((acc, key) => {
        const value = path[key];
        return value == null ? acc : acc.replace(`{${key}}`, String(value));
    }, baseUrl);
}

interface UseApiQueryExtraParams {
    enabled?: boolean;
    gcTime?: number;
    retry?: boolean;
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
}

export const useApiQuery = <P extends Path, M extends keyof paths[P]>(
    baseUrl: P,
    method: M,
    params?: OptionalRequestParams<P, M> extends undefined
        ? UseApiQueryExtraParams
        : OptionalRequestParams<P, M> & UseApiQueryExtraParams
) => {
    const acquireIdToken = useAcquireIdToken();
    const url = buildApiUrl(baseUrl, params?.path);

    const { isImpersonating, impersonateEmail, impersonateRoles } = useDevTools();

    const { data, isLoading, error, dataUpdatedAt, refetch } = useQuery<ResponseType<P, M>>({
        queryKey: [url, params?.query],
        enabled: params?.enabled ?? true,
        gcTime: params?.gcTime ?? undefined,
        retry: params?.retry,
        refetchOnMount: params?.refetchOnMount,
        refetchOnWindowFocus: params?.refetchOnWindowFocus,
        refetchOnReconnect: params?.refetchOnReconnect,
        queryFn: () =>
            apiCall<ResponseType<P, M>>({
                url,
                method: method.toString() as ApiCallProps['method'],
                query: params?.query,
                acquireToken: acquireIdToken,
                impersonate: isImpersonating ? { email: impersonateEmail, roles: impersonateRoles } : undefined,
            }),
    });

    return { data, loading: isLoading, error, dataUpdatedAt, refetch } as const;
};

export type MutationRequestParams<
    P extends Path,
    M extends PathMethod<P>,
    R extends RequestBody<P, M> = RequestBody<P, M>,
    // biome-ignore lint/complexity/noBannedTypes: cannot use record here
> = (OptionalRequestParams<P, M> extends undefined ? {} : OptionalRequestParams<P, M>) &
    // biome-ignore lint/complexity/noBannedTypes: cannot use record here
    (R extends undefined ? {} : { body: R });

export type UpdateCacheFn<P extends Path, M extends PathMethod<P>> = (
    data: ResponseType<P, M>,
    variables: MutationRequestParams<P, M>,
    queryClient: QueryClient
) => void;

interface ApiMutationOptions<P extends Path, M extends PathMethod<P>> {
    update?: UpdateCacheFn<P, M>;
}

export const useApiMutation = <P extends Path, M extends keyof paths[P]>(
    baseUrl: P,
    method: M,
    options?: ApiMutationOptions<P, M>
) => {
    const { isImpersonating, impersonateEmail, impersonateRoles } = useDevTools();
    const queryClient = useQueryClient();
    const acquireIdToken = useAcquireIdToken();
    const { mutateAsync, isPending, error } = useMutation<
        ResponseType<P, M>,
        DefaultError,
        MutationRequestParams<P, M>
    >({
        mutationKey: [baseUrl],
        mutationFn: (variables) => {
            const url = buildApiUrl(baseUrl, variables.path);
            return apiCall<ResponseType<P, M>>({
                url,
                method: method.toString() as ApiCallProps['method'],
                query: variables.query,
                body: variables.body,
                acquireToken: acquireIdToken,
                impersonate: isImpersonating ? { email: impersonateEmail, roles: impersonateRoles } : undefined,
            });
        },
    });

    const update = useCallback(
        async (params: MutationRequestParams<P, M>): Promise<ResponseType<P, M>> => {
            try {
                const data = await mutateAsync(params);
                const url = buildApiUrl(baseUrl, params.path);

                if (typeof options?.update === 'function') {
                    options.update(data, params, queryClient);
                } else {
                    queryClient.setQueryData([url, params.query], data);
                }

                return data;
            } catch (error: unknown) {
                datadogRum.addError(error, { apiRoute: baseUrl, method });
                throw error;
            }
        },
        [baseUrl, method, mutateAsync, options, queryClient]
    );

    return [update, { loading: isPending, error }] as const;
};
